import { toast } from "react-toastify";
import { getAccountsUrl } from "../utils/accountsUrl";
import LocalStorageService from "../../src/core/services/local-storage.service";
import { useState, useEffect } from "react";
import { globalCountryDetailsDictionary } from "../core/global/global-country-details";
import { getIntelUrl } from "../utils/intelUrl";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { multipleSearchSeparators } from "@containers/SearchHistory/constants";
import { CountryCodeMaps } from "@core/dictionary/specific-country-list";
import { DATASET_TYPE_MAPPING } from "@core/dictionary/global";
export const multiplyIdentifier = "###";

const localStorageService = LocalStorageService.getService();
const globalErrorHandling = (data) => {
  if (data.status === 401) {
    const emailParams = window.location.search;
    const errMsg = data?.data?.message;
    toast.error(errMsg ?? "Your session has been expired. Please login again");
    setTimeout(() => {
      localStorageService.clearCompleteStorage();
      window.location = !!emailParams && emailParams !== "" ? getAccountsUrl() + "?&authentication=false&" + emailParams.replace("?", "") : getAccountsUrl() + "?&authentication=false";
    }, 5000);
  } else {
    const errMsg = data?.data?.message ?? "Something Went Wrong!";
    toast.error(errMsg);
  }
};
export default globalErrorHandling;

export const isJsonResponse = (request) => (request.getResponseHeader("Content-Type") || "").includes("application/json");

// XHR file downloads set responseType = "blob", so a JSON error body arrives as a Blob and
// globalErrorHandling's data?.data?.message lookup always misses it. Read the body first and
// leave message undefined when it is not JSON, so the callee keeps its own default message.
export const handleDownloadError = async (request) => {
  let message;
  try {
    const responseText = request.response instanceof Blob ? await request.response.text() : request.response;
    message = JSON.parse(responseText)?.message;
  } catch {
    // response was not JSON, fall back to the generic messages below
  }
  if (request.status === 401) {
    globalErrorHandling({ status: 401, data: { message } });
    return;
  }
  toast.error(message || "Something Went Wrong!");
};

export function formatNumber(number) {
  if (isNaN(number)) {
    return "Invalid number";
  }
  if (number < 1000) {
    return number.toString();
  } else if (number < 1000000) {
    return (number / 1000).toFixed(2) + "K";
  } else if (number < 1000000000) {
    return (number / 1000000).toFixed(2) + "M";
  } else {
    return (number / 1000000000).toFixed(2) + "B";
  }
}

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup function to clear the timeout if the value changes before the delay
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
function uppercaseParentheses(text) {
  return text.replace(/\(([^)]+)\)/g, (match, p1) => `(${p1.toUpperCase()})`);
}
export const formatStringCase = (key, isUpperCase, isTitleCase, isLowerCase, isParanthesesCapital, isNoTransform) => {
  if (!key) return;
  key = key.toString();
  if (key.includes(multiplyIdentifier)) {
    return key.split(multiplyIdentifier).map((item, index) => (
      <span key={item + index}>
        {item} {index + 1 < key.split(multiplyIdentifier).length ? <span className="text-lowercase font-weight-bold">x</span> : ""}
      </span>
    ));
  }
  if (isNoTransform) {
    return <span className="text-no-transform">{key}</span>;
  } else if (isUpperCase) {
    return key.toUpperCase();
  } else if (isTitleCase) {
    return <span className="text-capitalize">{key.toLowerCase()}</span>;
  } else if (isLowerCase) {
    return <span className="text-lowercase">{key.toLowerCase()} </span>;
  } else if (isParanthesesCapital) {
    return <span className="text-capitalize">{uppercaseParentheses(key.toLowerCase())}</span>;
  } else if (isParanthesesCapital === false) {
    return <span className="text-no-transform">{handleUnits(key.toLowerCase())}</span>;
  }
  return key;
};

export const fetchDataSourceHandler = (header) => {
  let dataSourceOptionDetails = [];
  for (const prop in globalCountryDetailsDictionary) {
    let dataSourceOptionObj = {};
    dataSourceOptionObj.value = globalCountryDetailsDictionary[prop].localProduct;
    dataSourceOptionObj.label = globalCountryDetailsDictionary[prop].countryName;
    dataSourceOptionObj.url = "/" + globalCountryDetailsDictionary[prop].countryPathName;
    dataSourceOptionObj.href = getIntelUrl() + globalCountryDetailsDictionary[prop].countryPathName;
    dataSourceOptionObj.companyUrlSmall = "country=" + globalCountryDetailsDictionary[prop].country_small + "&type=" + globalCountryDetailsDictionary[prop].countryType;
    dataSourceOptionObj.companyUrlCaps = "country=" + globalCountryDetailsDictionary[prop].country_caps + "&type=" + globalCountryDetailsDictionary[prop].countryType;
    dataSourceOptionObj.countryName = globalCountryDetailsDictionary[prop].country_CamelCase;
    dataSourceOptionObj.countryCode = globalCountryDetailsDictionary[prop].countryCodeFlag;
    dataSourceOptionObj.dataSourceInfo = fetchDataSourceInfo(globalCountryDetailsDictionary[prop].product_id, header);
    dataSourceOptionObj.localProduct = globalCountryDetailsDictionary[prop].localProduct;
    dataSourceOptionObj.countryUrlDashboard = globalCountryDetailsDictionary[prop].countryUrlDashboard + "&type=" + globalCountryDetailsDictionary[prop].countryType;
    dataSourceOptionObj.countryType = globalCountryDetailsDictionary[prop].countryType;
    dataSourceOptionObj.continent = globalCountryDetailsDictionary[prop].continent;
    dataSourceOptionObj.isChecked = false;
    dataSourceOptionObj.dataSource = globalCountryDetailsDictionary[prop].dataSource;
    dataSourceOptionObj.countryCodeImage = globalCountryDetailsDictionary[prop]?.countryCodeImage ?? "";
    dataSourceOptionObj.coProductsUrl = globalCountryDetailsDictionary[prop].coProductsUrl ? globalCountryDetailsDictionary[prop].coProductsUrl : "";
    dataSourceOptionDetails.push(dataSourceOptionObj);
  }
  const dataSourceList = {};
  for (const key in globalCountryDetailsDictionary) {
    const parts = key.split("_");
    const country = globalCountryDetailsDictionary[key]?.countryCodeFlag ? globalCountryDetailsDictionary[key].countryCodeFlag.toLowerCase() : parts[0];
    const operation = parts[1];

    if (!dataSourceList[country]) {
      dataSourceList[country] = {};
    }
    let dataSourceOptionObj = {};
    dataSourceOptionObj.value = globalCountryDetailsDictionary[key].localProduct;
    dataSourceOptionObj.label = globalCountryDetailsDictionary[key].countryName;
    dataSourceOptionObj.url = "/" + globalCountryDetailsDictionary[key].countryPathName;
    dataSourceOptionObj.href = getIntelUrl() + globalCountryDetailsDictionary[key].countryPathName;
    dataSourceOptionObj.companyUrlSmall = "country=" + globalCountryDetailsDictionary[key].country_small + "&type=" + globalCountryDetailsDictionary[key].countryType;
    dataSourceOptionObj.companyUrlCaps = "country=" + globalCountryDetailsDictionary[key].country_caps + "&type=" + globalCountryDetailsDictionary[key].countryType;
    dataSourceOptionObj.countryName = globalCountryDetailsDictionary[key].country_CamelCase;
    dataSourceOptionObj.countryCode = globalCountryDetailsDictionary[key].countryCodeFlag;
    dataSourceOptionObj.dataSourceInfo = fetchDataSourceInfo(globalCountryDetailsDictionary[key].product_id, header);
    dataSourceOptionObj.continent = globalCountryDetailsDictionary[key].continent;
    dataSourceOptionObj.localProduct = globalCountryDetailsDictionary[key].localProduct;
    dataSourceOptionObj.countryUrlDashboard = globalCountryDetailsDictionary[key].countryUrlDashboard + "&type=" + globalCountryDetailsDictionary[key].countryType;
    dataSourceOptionObj.countryType = globalCountryDetailsDictionary[key].countryType;
    dataSourceOptionObj.isChecked = false;
    dataSourceOptionObj.dataSource = globalCountryDetailsDictionary[key].dataSource;
    dataSourceOptionObj.countryCodeImage = globalCountryDetailsDictionary[key]?.countryCodeImage ?? "";
    dataSourceOptionObj.coProductsUrl = globalCountryDetailsDictionary[key].coProductsUrl ? globalCountryDetailsDictionary[key].coProductsUrl : "";
    if (globalCountryDetailsDictionary[key]["type"] && globalCountryDetailsDictionary[key]["type"] === "single") {
      dataSourceList[country]["import"] = dataSourceOptionObj;
    } else {
      dataSourceList[country][operation] = dataSourceOptionObj;
    }
  }
  delete dataSourceList["multi"];
  return { dataSourceList, dataSourceOptionDetails };
};

const fetchDataSourceInfo = (productId, intelHeaderDataSource) => {
  let dataSourceInfo = {};
  if (!!intelHeaderDataSource && !!intelHeaderDataSource.length) {
    for (let i = 0; i < intelHeaderDataSource.length; i++) {
      if (!!productId && !!intelHeaderDataSource && intelHeaderDataSource[i].product_id.toLowerCase() === productId.toLowerCase()) {
        dataSourceInfo = {
          is_subscribed: intelHeaderDataSource[i].is_subscribed ? intelHeaderDataSource[i].is_subscribed : false,
          exports_count: intelHeaderDataSource[i].exports_count ? intelHeaderDataSource[i].exports_count : null,
          imports_count: intelHeaderDataSource[i].imports_count || null,
          total_count: !!intelHeaderDataSource[i].exports_count && !!intelHeaderDataSource[i].imports_count ? intelHeaderDataSource[i].exports_count + intelHeaderDataSource[i].imports_count : !!intelHeaderDataSource[i].exports_count && !intelHeaderDataSource[i].imports_count ? intelHeaderDataSource[i].exports_count : !!intelHeaderDataSource[i].imports_count && !intelHeaderDataSource[i].exports_count ? intelHeaderDataSource[i].imports_count : null,
        };
      }
    }
  }
  return dataSourceInfo;
};

export const displayCount = (number, decPlaces) => {
  decPlaces = Math.pow(10, decPlaces);
  // Enumerate number abbreviations
  let abbrev = ["K", "M", "B", "T"];
  // Go through the array backwards, so we do the largest first
  for (let i = abbrev.length - 1; i >= 0; i--) {
    // Convert array index to "1000", "1000000", etc
    let size = Math.pow(10, (i + 1) * 3);
    // If the number is bigger or equal do the abbreviation
    if (size <= number) {
      // Here, we multiply by decPlaces, round, and then divide by decPlaces.
      // This gives us nice rounding to a particular decimal place.
      number = Math.round((number * decPlaces) / size) / decPlaces;
      // Handle special case where we round up to the next abbreviation
      if (number === 1000 && i < abbrev.length - 1) {
        number = 1;
        i++;
      }
      // Add the letter for the abbreviation
      number += abbrev[i];
      // We are done... stop
      break;
    }
  }
  return number;
};

const dot = () => ({
  alignItems: "center",
  display: "flex",
});

export const searchTypeCustomStyles = {
  control: (base) => ({
    ...base,
    height: 30,
    width: 105,
    minHeight: 30,
    backgroundColor: "#fff",
    fontSize: "12px",
    fontWeight: 500,
    border: "none",
    boxShadow: "none",
  }),
  menu: (base) => ({
    ...base,
    fontSize: "12px",
    fontWeight: 500,
    padding: "8px",
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    height: 30,
    padding: "0px 8px 0px 0px",
    justifyContent: "center !important",
    alignItems: "center !important",
  }),
  valueContainer: (styles) => ({
    ...styles,
    justifyContent: "start !important",
    alignItems: "center !important",
  }),
  option: (base, { isDisabled, isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isDisabled ? null : isSelected ? "rgba(111, 8, 241, 0.1)" : isFocused ? "" : null,
    color: isDisabled ? null : isSelected ? "#1a1722" : isFocused ? "#1a1722" : null,
    cursor: "pointer !important",
    margin: "4px 0",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: "rgba(111, 8, 241, 0.1)",
    },
  }),
  input: (styles) => ({ ...styles, ...dot() }),
  placeholder: (styles) => ({ ...styles, ...dot("#ccc") }),
  singleValue: (styles, { data }) => ({ ...styles, ...dot(data.color) }),
};

export function includePendo(email, orgName) {
  window.pendo.initialize({
    visitor: {
      id: email,
    },
    account: {
      id: orgName,
    },
  });
}

export function includeSentry(userDetails) {
  if (!window.__sentryInitialized) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_KEY,
      integrations: [new BrowserTracing(), new Sentry.Replay()],
      release: import.meta.env.VITE_APP_BRANCH + "_" + import.meta.env.VITE_APP_COMMIT,
      debug: false,
      ignoreErrors: ["TypeError: Failed to fetch", "TypeError: NetworkError when attempting to fetch resource.", "TypeError: Cancelled", "TypeError: Request failed with status code 401", "Request failed with status code 401", "Non-Error exception captured", "Non-Error promise rejection captured", "Network Error", "Unhandled Promise Rejection: Network Error"],
      initialScope: (scope) => {
        scope.setTags({ userDetails: JSON.stringify(userDetails) });
        return scope;
      },
      environment: import.meta.env.VITE_HOST_ENV ? import.meta.env.VITE_HOST_ENV : "",
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: 1, // performance trace sampling
      replaysSessionSampleRate: 0.5, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
    window.__sentryInitialized = true;
  }
}

export const getUserCurrentPlan = (plan) => {
  if (plan?.includes("advance")) {
    return "for last 2 years";
  } else if (plan?.includes("pro")) {
    return "for last 1 year";
  }
};

export const titleCaseDataSource = (data) => {
  if (!data || typeof data !== "string") return;
  if (data.split(" ")?.[0] && data.split(" ")?.[1]) {
    let country = data.split(" ")[0];
    let countryType = data.split(" ")[1];
    let countryLowerCase = country?.toLowerCase();
    let trade_direction = countryType?.toLowerCase()?.endsWith("s") ? countryType?.toLowerCase()?.slice(0, -1) : countryType?.toLowerCase();
    let countryCode = CountryCodeMaps[countryLowerCase]?.toLowerCase();
    if (globalCountryDetailsDictionary[`${countryCode}_${trade_direction}`]) {
      return country + " " + countryType + " " + (DATASET_TYPE_MAPPING[`${countryCode}_${trade_direction}`] ?? "");
    }
    if (country.length < 3) {
      return country.toUpperCase() + " " + countryType[0].toUpperCase() + countryType.slice(1).toLowerCase();
    } else {
      return country[0].toUpperCase() + country.slice(1).toLowerCase() + " " + countryType[0].toUpperCase() + countryType.slice(1).toLowerCase();
    }
  } else return data[0].toUpperCase() + data.slice(1).toLowerCase();
};

export const getTitleCase = (text) => {
  if (typeof text !== "string") return text;
  let strArr = [];
  text
    .replace(/  +/g, " ")
    .split(" ")
    .forEach((word) => {
      if (word?.[0]) strArr.push(word[0]?.toUpperCase() + word.slice(1)?.toLowerCase());
    });
  return strArr.join(" ");
};

export const getChemicalSearchName = (text) => {
  if (typeof text !== "string") return text;
  let searchName = [];
  text
    .replace(/  +/g, " ")
    .split(multipleSearchSeparators)
    .forEach((word) => {
      searchName.push(word.split("|")[0]);
    });
  return getTitleCase(searchName.join(` ${multipleSearchSeparators} `));
};

export const handleUnits = (text) => {
  return text.replace(/\b(\w)(?!\()/g, (_, p1) => p1.toUpperCase()).replace(/\(([^)]*)\)/g, (match) => match.toLowerCase());
};

export const IEC_GRID_KEYS = ["derived_importer_id", "derived_shipper_iec_number"];

const DRIPCAPITAL_DOMAIN = "@dripcapital.com";

export const isStarterPlan = (creditDetails) => !!creditDetails?.data?.some((d) => d?.is_active && d?.user_active_plan?.toLowerCase()?.includes("starter"));

export const canViewIndiaIec = (email, creditDetails) => (email ?? "").toLowerCase().includes(DRIPCAPITAL_DOMAIN) && !isStarterPlan(creditDetails);
